import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import AIMessageBubble from './AIMessageBubble';
import { Trash2 } from 'lucide-react';

const ChatMessages = ({ 
  messages, 
  isLoading, 
  businessType, 
  messagesEndRef, 
  chatMessagesRef,
  onDeleteMessage,
  currentConversationId,
  voiceState,
  onTTSStateChange,
  onAutoRestart,
  inputMode,
  onInputModeChange,
  onVoiceStateChange,
  onMarkMessagesAsRead
}) => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const [hoveredMessage, setHoveredMessage] = useState(null);

  // Handle deleting a single message
  const handleDeleteMessage = async (messageId) => {
    if (!isAuthenticated()) {
      alert('You must be logged in to delete messages');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/chat-history/messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include', // Use cookie-based authentication
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('Message deleted successfully');
        if (onDeleteMessage) {
          onDeleteMessage(messageId);
        }
      } else {
        const error = await response.json();
        console.error('Error deleting message:', error);
        alert('Failed to delete message. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // Helper to render markdown-like content for AI answers
  function renderMarkdown(content) {
    // Replace H1 (lines starting with # )
    content = content.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    // Replace H2 (lines starting with ## )
    content = content.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    // Replace bold **text**
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Replace bullet points
    content = content.replace(/^\s*[-*] (.*$)/gim, '<li>$1</li>');
    // Replace newlines with <br/>
    content = content.replace(/\n/g, '<br/>');
    // Wrap <li> in <ul>
    content = content.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
    return content;
  }

  function formatAnswer(content, index) {
    return (
      <div style={{ textAlign: 'left' }}>
        <div dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      <div className="chat-messages" ref={chatMessagesRef}>
      {messages.map((message, index) => (
        <div 
          key={index} 
          className={`message ${message.type === 'question' ? 'chat-question' : message.type === 'answer' ? 'chat-answer' : 'user-message'}`}
          style={{ position: 'relative' }}
          onMouseEnter={() => setHoveredMessage(index)}
          onMouseLeave={() => setHoveredMessage(null)}
        >
          {message.type === 'answer' ? (
            <AIMessageBubble
              content={message.content}
              theme={theme}
              index={index}
              formatAnswer={formatAnswer}
              voiceState={voiceState}
              onTTSStateChange={onTTSStateChange}
              onAutoRestart={onAutoRestart}
              inputMode={inputMode}
              onInputModeChange={onInputModeChange}
              onVoiceStateChange={onVoiceStateChange}
              onMarkAsRead={onMarkMessagesAsRead}
              messageSource={message.source || 'fromKeyboardToText'}
            />
          ) : message.type === 'question' ? (
            <div className="bubble question-bubble" style={{
              background: '#f8fafc',
              color: '#1e293b',
              border: '1px solid #e2e8f0',
              maxWidth: '70%',
              padding: '12px 16px',
              borderRadius: '18px',
              borderBottomLeftRadius: '8px',
              wordWrap: 'break-word',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              lineHeight: '1.4',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              animation: 'slideIn 0.3s ease-out',
              animationDelay: `${index * 100}ms`
            }}>{message.content}</div>
          ) : (
            <div className="bubble user-bubble" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              maxWidth: '70%',
              padding: '12px 16px',
              borderRadius: '18px',
              borderBottomRightRadius: '8px',
              wordWrap: 'break-word',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              lineHeight: '1.4',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              animation: 'slideIn 0.3s ease-out',
              animationDelay: `${index * 100}ms`
            }}>{message.content}</div>
          )}
          
          {/* Delete Button - Only show for authenticated users and when hovering */}
          {isAuthenticated() && hoveredMessage === index && message.id && (
            <button
              onClick={() => handleDeleteMessage(message.id)}
              style={{
                position: 'absolute',
                top: '8px',
                right: message.type === 'answer' ? '8px' : '-40px',
                background: 'rgba(239, 68, 68, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                transition: 'all 0.2s ease',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.background = 'rgba(239, 68, 68, 1)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'scale(1)';
                e.target.style.background = 'rgba(239, 68, 68, 0.9)';
              }}
              title="Delete this message"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      ))}
      
      {isLoading && (
        <div className="simple-thinking-container">
          {/* Simple AI Status */}
          <div className="simple-ai-status">
            <div className="simple-logo">Oakland AI</div>
            <div className="simple-status-text">Oakland AI is thinking...</div>
          </div>
          
          {/* Simple Progress Bar */}
          <div className="simple-progress">
            <div className="simple-progress-bar">
              <div className="simple-progress-fill"></div>
            </div>
          </div>
          
          {/* Simple Loading Dots */}
          <div className="simple-loading-dots">
            <div className="simple-dot"></div>
            <div className="simple-dot"></div>
            <div className="simple-dot"></div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
      </div>
    </>
  );
};

export default ChatMessages; 